import axios from 'axios';
import { API } from './constants';
import { getSessionToken, TOKEN_KEY } from './authSession';

const DEVICE_OWNER_KEY = 'mygoal-push-device-owner';
const WORKER_TIMEOUT = 8000;
const REQUEST_TIMEOUT = 10000;
let prepared;
let generation = 0;
let deviceChanges = Promise.resolve();

export const browserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Amsterdam';

const session = userId => ({ userId: String(userId), token: getSessionToken(), generation });
const assertSession = context => {
  const currentUser = localStorage.getItem('mygoal-user-id');
  if (!context.token || context.token !== getSessionToken() || context.generation !== generation ||
      (currentUser && currentUser !== context.userId)) {
    throw new Error('Your account changed. Reopen reminder settings and try again.');
  }
};
const requestOptions = context => {
  assertSession(context);
  return { timeout: REQUEST_TIMEOUT, headers: { Authorization: `Bearer ${context.token}` } };
};
const bounded = promise => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('Notifications are not ready. Reopen MyGoal and try again.')), WORKER_TIMEOUT);
  Promise.resolve(promise).then(resolve, reject).finally(() => clearTimeout(timer));
});
const changeDevice = operation => {
  const result = deviceChanges.then(operation);
  deviceChanges = result.catch(() => {});
  return result;
};
const announce = name => window.dispatchEvent(new Event(name));
const subscriptionDetails = subscription => {
  const { endpoint, keys } = subscription.toJSON();
  return { endpoint, keys };
};
const ownedBy = userId => localStorage.getItem(DEVICE_OWNER_KEY) === String(userId);
const getRegistration = async () => {
  // Registration and ready can both remain pending when installation fails.
  const registration = await bounded(navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }));
  return registration.active ? registration : bounded(navigator.serviceWorker.ready);
};
const getSubscription = registration => bounded(registration.pushManager.getSubscription());
const loadSettings = async context => {
  const response = await axios.get(`${API}/reminders/settings`, requestOptions(context));
  assertSession(context);
  return response.data;
};
const prepare = context => {
  assertSession(context);
  if (!prepared || prepared.userId !== context.userId || prepared.token !== context.token || prepared.generation !== context.generation) {
    const promise = Promise.all([loadSettings(context), getRegistration()]).then(([settings, registration]) => {
      assertSession(context);
      return { ...settings, registration };
    });
    prepared = { ...context, promise };
    promise.catch(() => { if (prepared?.promise === promise) prepared = null; });
  }
  return prepared.promise;
};
const restore = async context => {
  assertSession(context);
  if (!ownedBy(context.userId)) return false;
  if (Notification.permission !== 'granted') {
    localStorage.removeItem(DEVICE_OWNER_KEY);
    return false;
  }
  const settings = await prepare(context);
  const subscription = await getSubscription(settings.registration);
  assertSession(context);
  if (!subscription) {
    localStorage.removeItem(DEVICE_OWNER_KEY);
    return false;
  }
  // A browser subscription alone does not prove that the server can deliver.
  await axios.post(`${API}/reminders/subscriptions`, subscriptionDetails(subscription), requestOptions(context));
  assertSession(context);
  return true;
};

const WebPushService = {
  isSupported: () => Boolean(window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window),
  async getSettings(userId) {
    return loadSettings(session(userId));
  },
  async prepare(userId) {
    if (!this.isSupported()) return null;
    return prepare(session(userId));
  },
  async enable(userId, preferences) {
    if (!this.isSupported()) throw new Error('On iPhone, add MyGoal to your Home Screen and open it there to enable reminders. Other devices need a browser that supports notifications.');
    const context = session(userId);
    assertSession(context);
    // Keep this call before any await, including the device-operation queue:
    // Safari requires permission to be requested directly from the user's tap.
    const permission = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
    if (permission !== 'granted') throw new Error('Allow notifications in your device settings to receive reminders.');
    return changeDevice(async () => {
      assertSession(context);
      const settings = await prepare(context);
      const savedPreferences = { ...(preferences || settings.preferences), timezone: browserTimezone() };
      let subscription = await getSubscription(settings.registration);
      assertSession(context);
      // Never reuse a previous account's endpoint, including an interrupted logout.
      if (subscription && !ownedBy(userId)) {
        await subscription.unsubscribe();
        assertSession(context);
        subscription = null;
      }
      let created = false;
      try {
        if (!subscription) {
          const encoded = settings.public_key.replace(/-/g, '+').replace(/_/g, '/');
          const bytes = Uint8Array.from(atob(encoded + '='.repeat((4 - encoded.length % 4) % 4)), c => c.charCodeAt(0));
          subscription = await settings.registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes });
          created = true;
        }
        assertSession(context);
        await axios.put(`${API}/reminders/settings`, savedPreferences, requestOptions(context));
        assertSession(context);
        await axios.post(`${API}/reminders/subscriptions`, subscriptionDetails(subscription), requestOptions(context));
        assertSession(context);
        localStorage.setItem(DEVICE_OWNER_KEY, context.userId);
        settings.preferences = savedPreferences;
        announce('mygoal-notifications-enabled');
        return savedPreferences;
      } catch (error) {
        // A failed first subscription must not look enabled or leave an orphan
        // endpoint receiving notifications after a partially completed request.
        if (created) await subscription.unsubscribe().catch(() => {});
        throw error;
      }
    });
  },
  async restore(userId) {
    if (!this.isSupported()) return false;
    const context = session(userId);
    return changeDevice(() => restore(context));
  },
  async isEnabled(userId) {
    return this.restore(userId);
  },
  async disable(userId) {
    const context = session(userId);
    return changeDevice(async () => {
      assertSession(context);
      if (!ownedBy(userId)) return;
      // Disabling also works offline: unregister locally even if the API fails.
      const registration = await bounded(navigator.serviceWorker.getRegistration('/'));
      const subscription = registration && await getSubscription(registration);
      assertSession(context);
      if (subscription) {
        const results = await Promise.allSettled([
          axios.delete(`${API}/reminders/subscriptions`, { ...requestOptions(context), data: { endpoint: subscription.endpoint } }),
          subscription.unsubscribe(),
        ]);
        assertSession(context);
        if (results.every(result => result.status === 'rejected')) throw new Error('Could not disable reminders. Reconnect and try again.');
      }
      localStorage.removeItem(DEVICE_OWNER_KEY);
      announce('mygoal-reminders-updated');
    });
  },
  async updatePreferences(userId, preferences) {
    const context = session(userId);
    const response = await axios.put(`${API}/reminders/settings`, preferences, requestOptions(context));
    assertSession(context);
    // Do not wait for a service worker just to save account preferences.
    if (prepared?.userId === context.userId && prepared.token === context.token) prepared = null;
    announce('mygoal-reminders-updated');
    return response.data?.preferences || preferences;
  },
  async test(userId) {
    const context = session(userId);
    return changeDevice(async () => {
      assertSession(context);
      if (!this.isSupported() || !ownedBy(userId) || Notification.permission !== 'granted') throw new Error('Enable reminders on this device first.');
      const settings = await prepare(context);
      const subscription = await getSubscription(settings.registration);
      assertSession(context);
      if (!subscription) {
        localStorage.removeItem(DEVICE_OWNER_KEY);
        throw new Error('Enable reminders on this device first.');
      }
      await axios.post(`${API}/reminders/test`, { endpoint: subscription.endpoint }, requestOptions(context));
      assertSession(context);
    });
  },
  clearDevice() {
    generation += 1;
    localStorage.removeItem(DEVICE_OWNER_KEY);
    prepared = null;
    const clearedToken = getSessionToken();
    return changeDevice(async () => {
      // Another tab may already have enabled a new account on this browser.
      const canClear = () => !localStorage.getItem(DEVICE_OWNER_KEY) && getSessionToken() === clearedToken;
      if (!navigator.serviceWorker || !canClear()) return;
      const registration = await bounded(navigator.serviceWorker.getRegistration('/'));
      const subscription = registration && await getSubscription(registration);
      if (subscription && canClear()) await subscription.unsubscribe();
    }).catch(() => {});
  },
};
window.addEventListener('mygoal-session-cleared', () => WebPushService.clearDevice());
window.addEventListener('storage', event => {
  if (event.key === TOKEN_KEY || event.key === null) WebPushService.clearDevice();
});
export default WebPushService;
